import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { GetCohortQuery } from "generated-graphql/graphql";
import { useParams, useSearchParams } from "react-router-dom";

export const PERSON_FRAGMENT = gql(`
  fragment PersonFragment on Person {
    id
    first
    last
    email
    tenantId
    title
    city
    state
    streetAddress1
    zip
    phones {
      number
      type
    }
  }
`);

export const GET_COHORT = gql(`
query GetCohort($cohortId: ID!) {
  cohort(id: $cohortId) {
    id
    type
    tenantId
    tenant {
      id
      name
    }
    status
    reportingYear
    alternateIdId
    fdId
    fireDepartment {
      id
      name
    }
    lepcId
    lepc {
      id
      name
    }
    credentialUsername
    state
    createdAt
    updatedAt
    members {
      id
      status
      createdAt
      updatedAt
      tierIIReportId
      tierIIReport {
        id
        tags
      }
      facility {
        id
        name
        city
        state
        streetAddress1
        county
        companyName
        customerFacilityId
        tenantId
        tenant {
          id
          name
        }
        fireDepartment {
          id
          name
        }
        lepc {
          id
          name
        }
        contacts {
          id
          reportingRoles
          person {
            ...PersonFragment
          }
        }
      }
    }
    workflows {
      id
      status
      type
      activities {
        id
        type
        status
        executionPlan
        description
        jobs {
          id
          status
          task
          action
          taskUrl
          createdAt
          updatedAt
        }
      }
    }
    currentWorkflow {
      id
      status
      tags {
        id
        name
      }
      comments {
        id
        comment
        createdAt
        updatedAt
        createdBy
        user {
          id
          email
          person {
            first
            last
          }
        }
      }
      activities {
        id
        type
        status
        executionPlan
        description
        championId
        nonChampionIds
        cohortFacilityIds
        reportDetailsExtended
        output
        inputs {
          id
          activityId
          label
          shortName
          value
          required
          type
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            fileExtension
            updatedAt
            createdAt
            sizeMegabytes
            documentTags {
              id
              name
              type
            }
          }
        }
        currentJob {
          id
          status
          task
          action
          taskUrl
          createdAt
        }
        jobs {
          id
          status
          task
          action
          taskUrl
          createdAt
          updatedAt
        }
        payments {
          id
          status
          paidAt
          tenantId
          amount
          lobMail {
            id
            lobId
            status
            type
          }
        }
      }
      currentActivityId
      currentActivity {
        id
        type
        status
        executionPlan
        description
        assigneeId
        currentJobId
        nonChampionIds
        currentJob {
          id
          taskArn
          taskUrl
          status
          task
          action
          createdAt
        }
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        documents {
          document {
            id
            title
            description
            storageLink
            documentType
            fileExtension
            updatedAt
            createdAt
            sizeMegabytes
            documentTags {
              id
              name
              type
            }
          }
        }
      }
    }
  }
}`);

type MinimalOptions = {
  onCompleted?: (data: GetCohortQuery) => void;
};

export function useCohortDetails(options?: MinimalOptions) {
  const { cohortId } = useParams<{ cohortId: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");

  const query = useQuery(GET_COHORT, {
    variables: {
      cohortId: cohortId ?? "",
    },
    skip: !cohortId,
    onCompleted: options?.onCompleted,
  });

  if (query.error) throw query.error;

  return query;
}
